import { DnsOutlined } from '@mui/icons-material';
import { Box } from '@mui/material';
import React, { memo } from 'react';
import ReactFlow, { addEdge, applyEdgeChanges, applyNodeChanges, Background, useReactFlow, ReactFlowProvider } from 'react-flow-renderer';
import CustomNode from './CustomNode';
import { grey } from '@mui/material/colors';
import theme from '../theme';
import { renderItem, getItemTitle, getItemZabbix } from './items';
import api from '../services/api';
import GraphNode from './GraphNode';
import ShapeNode from './ShapeNode';

const nodeTypes = {
  custom: CustomNode,
  graph: GraphNode,
  shape: ShapeNode,
};

// drawtype do Zabbix → estilos de link
function getLinkStyle(drawtype, color) {
  const hex = `#${color}`;
  switch (Number(drawtype)) {
    case 2: // Bold
      return { strokeWidth: 4, stroke: hex };
    case 3: // Dotted
      return { strokeWidth: 2, stroke: hex, strokeDasharray: '3 3' };
    case 4: // Dashed
      return { strokeWidth: 2, stroke: hex, strokeDasharray: '8 4' };
    default: // Solid (0)
      return { strokeWidth: 2, stroke: hex };
  }
}

// Resolve estilo do link baseado em linktriggers ativos (fiel ao Zabbix)
function resolveLinkStyle(link, activeTriggerIds, problemsByTrigger) {
  const activeLinktriggers = (link.linktriggers || [])
    .filter(lt => activeTriggerIds.has(lt.triggerid));

  if (activeLinktriggers.length > 0) {
    const best = activeLinktriggers.reduce((a, b) => {
      const sevA = problemsByTrigger.get(a.triggerid)?.severity || 0;
      const sevB = problemsByTrigger.get(b.triggerid)?.severity || 0;
      return sevB > sevA ? b : a;
    });
    return getLinkStyle(best.drawtype, best.color);
  }
  return getLinkStyle(link.drawtype, link.color);
}

function Flow({ map, hosts, contextMenu, imgs, cod_empresa, host, problems, items, onLoadSubMap, onHostClick }) {

  if (map == false || hosts == false) {
    return (<></>);
  }
  const reactFlowRef = React.useRef(null);
  const lastFocusedHost = React.useRef(null);
  const flowReady = React.useRef(false);
  const pendingFocus = React.useRef(null);

  const mapWithHost = map.selements.map(el => ({
    ...el,
    elements: el.elements.length > 0 && el.elementtype === '0'
      ? hosts.filter(h => parseInt(h[1]) == parseInt(el.elements[0].hostid))
      : []
  }));

  const { setCenter } = useReactFlow();

  const doFocus = React.useCallback((x, y) => {
    setTimeout(() => setCenter(x, y, { duration: 500, zoom: 1.5 }), 100);
  }, [setCenter]);

  // Quando ReactFlow inicializa, aplica focus pendente (deep link)
  const onFlowInit = React.useCallback(() => {
    flowReady.current = true;
    if (pendingFocus.current) {
      const { x, y } = pendingFocus.current;
      pendingFocus.current = null;
      doFocus(x, y);
    }
  }, [doFocus]);

  // Foca no host selecionado (uma vez por host)
  React.useEffect(() => {
    if (host == null) {
      lastFocusedHost.current = null;
      return;
    }
    if (lastFocusedHost.current === host[1]) return;
    const found = mapWithHost.filter(node => node.elements.length > 0).find(el => el.elements[0][1] == host[1]);
    if (!found) return;
    lastFocusedHost.current = host[1];
    const x = parseInt(found.x);
    const y = parseInt(found.y);
    if (flowReady.current) {
      doFocus(x, y);
    } else {
      pendingFocus.current = { x, y };
    }
  });

  // label_location: -1 = map default, 0=bottom, 1=left, 2=right, 3=top
  const mapLabelLocation = Number(map.label_location) || 0;
  const resolveLabelLoc = (loc) => {
    const n = Number(loc);
    return (n === -1 || isNaN(n)) ? mapLabelLocation : n;
  };

  // === Nodes: hosts (type 0) ===
  const hostNodes = mapWithHost.filter(el => el.elementtype === '0').map((node) => ({
    id: node.selementid,
    elements: node.elements,
    type: 'custom',
    position: { x: Number(node.x), y: Number(node.y) },
    data: {
      iconid_off: imgs?.find(img => img.imageid == node.iconid_off)?.image,
      iconid_on: imgs?.find(img => img.imageid == node.iconid_on)?.image,
      iconid_disabled: imgs?.find(img => img.imageid == node.iconid_disabled)?.image,
      label: node.elements.length > 0 ? node.elements[0][2] : node.label || '',
      ip: node.elements.length > 0 ? node.elements[0][3] : '',
      status: node.elements.length > 0 ? node.elements[0][5] == "0" : '',
      model: node.elements.length > 0 ? node.elements[0][4] : '',
      hostid: node.elements.length > 0 ? node.elements[0][1] : null,
      contextMenu: contextMenu,
      cod_empresa: cod_empresa,
      label_location: resolveLabelLoc(node.label_location),
      problems: node.elements.length > 0 ? problems.filter(p => p.hostid == node.elements[0][1]) : []
    },
  }));

  // === Nodes: image elements (type 4) ===
  const imageNodes = map.selements.filter(el => el.elementtype === '4').map((node) => ({
    id: node.selementid,
    type: 'custom',
    position: { x: Number(node.x), y: Number(node.y) },
    draggable: false,
    data: {
      iconid_off: imgs?.find(img => img.imageid == node.iconid_off)?.image,
      iconid_on: imgs?.find(img => img.imageid == node.iconid_on)?.image,
      iconid_disabled: imgs?.find(img => img.imageid == node.iconid_disabled)?.image,
      label: node.label || '',
      ip: '',
      status: true,
      model: '',
      cod_empresa: cod_empresa,
      label_location: resolveLabelLoc(node.label_location),
      problems: []
    },
  }));

  // === Nodes: map/sub-map elements (type 1) ===
  const mapNodes = map.selements.filter(el => el.elementtype === '1').map((node) => ({
    id: node.selementid,
    type: 'custom',
    position: { x: Number(node.x), y: Number(node.y) },
    data: {
      iconid_off: imgs?.find(img => img.imageid == node.iconid_off)?.image,
      iconid_on: imgs?.find(img => img.imageid == node.iconid_on)?.image,
      iconid_disabled: imgs?.find(img => img.imageid == node.iconid_disabled)?.image,
      label: node.label || 'Sub-mapa',
      ip: '',
      status: true,
      model: '',
      cod_empresa: cod_empresa,
      label_location: resolveLabelLoc(node.label_location),
      problems: [],
      isSubMap: true,
      subMapId: node.elements?.[0]?.sysmapid,
    },
  }));

  // === Nodes: trigger elements (type 2) ===
  const triggerNodes = map.selements.filter(el => el.elementtype === '2').map((node) => ({
    id: node.selementid,
    type: 'custom',
    position: { x: Number(node.x), y: Number(node.y) },
    draggable: false,
    data: {
      iconid_off: imgs?.find(img => img.imageid == node.iconid_off)?.image,
      iconid_on: imgs?.find(img => img.imageid == node.iconid_on)?.image,
      iconid_disabled: imgs?.find(img => img.imageid == node.iconid_disabled)?.image,
      label: node.label || '',
      ip: '',
      status: true,
      model: '',
      cod_empresa: cod_empresa,
      label_location: resolveLabelLoc(node.label_location),
      problems: []
    },
  }));

  // === Nodes: host group elements (type 3) ===
  const hostGroupNodes = map.selements.filter(el => el.elementtype === '3').map((node) => ({
    id: node.selementid,
    type: 'custom',
    position: { x: Number(node.x), y: Number(node.y) },
    draggable: false,
    data: {
      iconid_off: imgs?.find(img => img.imageid == node.iconid_off)?.image,
      iconid_on: imgs?.find(img => img.imageid == node.iconid_on)?.image,
      iconid_disabled: imgs?.find(img => img.imageid == node.iconid_disabled)?.image,
      label: node.label || '',
      ip: '',
      status: true,
      model: '',
      cod_empresa: cod_empresa,
      label_location: resolveLabelLoc(node.label_location),
      problems: []
    },
  }));

  // === Shapes (retangulos, elipses, textos) ===
  const shapeNodes = (map.shapes || []).map((shape, idx) => ({
    id: `shape-${shape.sysmap_shapeid || idx}`,
    type: 'shape',
    position: { x: Number(shape.x), y: Number(shape.y) },
    draggable: false,
    selectable: false,
    zIndex: -1,
    data: {
      shapeType: Number(shape.type),
      width: Number(shape.width) || 100,
      height: Number(shape.height) || 50,
      text: shape.text || '',
      font_size: Number(shape.font_size) || 11,
      font_color: shape.font_color || '000000',
      background_color: shape.background_color || '',
      border_type: Number(shape.border_type) || 0,
      border_color: shape.border_color || '000000',
      border_width: Number(shape.border_width) || 1,
    },
  }));

  // === Lines decorativas → pares de anchor nodes + edges ===
  const lineAnchors = [];
  const lineEdges = [];
  (map.lines || []).forEach((line, idx) => {
    const id1 = `line-a-${idx}`;
    const id2 = `line-b-${idx}`;
    lineAnchors.push(
      {
        id: id1,
        type: 'default',
        position: { x: Number(line.x1 || 0), y: Number(line.y1 || 0) },
        draggable: false,
        selectable: false,
        style: { width: 0, height: 0, border: 'none', background: 'transparent', padding: 0 },
        data: {},
      },
      {
        id: id2,
        type: 'default',
        position: { x: Number(line.x2 || 0), y: Number(line.y2 || 0) },
        draggable: false,
        selectable: false,
        style: { width: 0, height: 0, border: 'none', background: 'transparent', padding: 0 },
        data: {},
      }
    );

    const lineColor = line.line_color ? `#${line.line_color}` : '#999';
    const lineWidth = Number(line.line_width) || 1;
    const dashArray = Number(line.line_type) === 2 ? '3 3'
      : Number(line.line_type) === 3 ? '8 4'
      : undefined;

    lineEdges.push({
      id: `line-edge-${idx}`,
      source: id1,
      target: id2,
      type: 'straight',
      style: {
        stroke: lineColor,
        strokeWidth: lineWidth,
        ...(dashArray ? { strokeDasharray: dashArray } : {}),
      },
      selectable: false,
      interactionWidth: 0,
    });
  });

  const nodes_raw = [...shapeNodes, ...lineAnchors, ...hostNodes, ...imageNodes, ...mapNodes, ...triggerNodes, ...hostGroupNodes];

  // === Links com drawtype + linktriggers ===
  const activeTriggerIds = new Set(problems.map(p => String(p.triggerid)));
  const problemsByTrigger = new Map(problems.map(p => [String(p.triggerid), p]));

  const links = map.links.map((link) => {
    const style = resolveLinkStyle(link, activeTriggerIds, problemsByTrigger);
    return {
      id: link.linkid,
      style,
      source: link.selementid1,
      target: link.selementid2,
      type: 'straight',
      rawLabel: link.label,
      linktriggers: link.linktriggers || [],
      defaultDrawtype: link.drawtype,
      defaultColor: link.color,
      smooth: false,
      labelShowBg: true,
      labelStyle: {
        fill: theme.palette.mode == 'light' ? grey[900] : grey[100],
        fontSize: 10,
      },
      labelBgStyle: {
        fill: theme.palette.mode == 'dark' ? grey[900] : grey[100],
        stroke: style.stroke,
        strokeWidth: 1,
        borderRadius: 3,
        opacity: 0.9,
      },
      labelBgPadding: [4, 2],
      labelBgBorderRadius: 3,
    };
  });

  const [nodes, setNodes] = React.useState(nodes_raw);
  const [edges, setEdges] = React.useState([...lineEdges, ...links]);

  React.useEffect(() => {
    setEdges((prevEdges) =>
      prevEdges.map((edge) => {
        if (!edge.rawLabel) return edge; // Skip line edges
        return {
          ...edge,
          label: renderItem(edge.rawLabel, items),
          itemid: getItemZabbix(edge.rawLabel, items),
          title: getItemTitle(edge.rawLabel, items)
        };
      })
    );
  }, [items]);

  React.useEffect(() => {
    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (!node.elements) return node;
        return {
          ...node,
          data: {
            ...node.data,
            problems: node.elements.length > 0 ? problems.filter(p => p.hostid == node.elements[0][1]) : []
          },
        };
      })
    );

    // Recalcula estilo dos links baseado em linktriggers ativos
    const trigIds = new Set(problems.map(p => String(p.triggerid)));
    const probByTrig = new Map(problems.map(p => [String(p.triggerid), p]));
    setEdges((prevEdges) =>
      prevEdges.map((edge) => {
        if (!edge.linktriggers) return edge;
        const active = edge.linktriggers.filter(lt => trigIds.has(lt.triggerid));
        let style;
        if (active.length > 0) {
          const best = active.reduce((a, b) => {
            const sevA = probByTrig.get(a.triggerid)?.severity || 0;
            const sevB = probByTrig.get(b.triggerid)?.severity || 0;
            return sevB > sevA ? b : a;
          });
          style = getLinkStyle(best.drawtype, best.color);
        } else {
          style = getLinkStyle(edge.defaultDrawtype, edge.defaultColor);
        }
        return { ...edge, style, labelBgStyle: { ...edge.labelBgStyle, stroke: style.stroke } };
      })
    );
  }, [problems]);

  const onNodesChange = React.useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes]
  );
  const onEdgesChange = React.useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );
  const onConnect = React.useCallback(
    (connection) => setEdges((eds) => addEdge(connection, eds)),
    [setEdges]
  );

  return (
    <div style={{ height: '92vh', width: '100%' }} ref={reactFlowRef}>
      <ReactFlow nodes={nodes} edges={edges}
        minZoom={0.1}
        maxZoom={6}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={onFlowInit}
        fitView
        nodeTypes={nodeTypes}
        snapToGrid={true}
        onNodeDoubleClick={(event, node) => {
          // Sub-map navigation
          if (node.data?.isSubMap && node.data?.subMapId && onLoadSubMap) {
            onLoadSubMap(node.data.subMapId);
            return;
          }
          // Host click → abre painel de items/graficos
          if (node.data?.hostid && onHostClick) {
            onHostClick(node.data.hostid, node.data?.label || '');
          }
        }}
        onEdgeClick={(event, edge) => {
          if (!edge.itemid) return;
          const x = event.nativeEvent.offsetX;
          const y = event.nativeEvent.offsetY;
          const id = edge.itemid[0];
          const newNode = {
            id: id,
            type: 'graph',
            data: {
              itemid: edge.itemid, cod_empresa: cod_empresa, title: edge.title,
              close: () => {
                setNodes((prevNodes) => prevNodes.filter((node) => node.id !== id));
              }
            },
            position: { x, y },
          };
          setNodes((prevNodes) => [...prevNodes, newNode]);
        }}
      >
        <Background gap={12} size={0.5} />
      </ReactFlow>
    </div>
  );
}

function ZabbixMap(props) {
  return (
    <ReactFlowProvider>
      <Flow {...props} />
    </ReactFlowProvider>
  );
}

export default memo(ZabbixMap);
